import tw from 'twin.macro'
import { GetStaticPaths, GetStaticProps } from 'next'
import apolloClient from '../../utils/apollo'
import { gql } from '@apollo/client'
import Head from 'next/head'
import { Post } from '../../utils/interfaces'
import {
  documentToReactComponents,
  NodeRenderer,
  Options,
} from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'
import { ReactNode } from 'react'
import Image from 'next/image'
import dayjs from 'dayjs'
import Link from 'next/link'
import contentful from '../../utils/contentful'
import { Document } from '@contentful/rich-text-types'

const getStaticProps: GetStaticProps = async ({ params }) => {
  if (params?.slug) {
    const posts = await apolloClient.query({
      query: gql`
        query {
          blogPostCollection(where: {slug: "${params?.slug}"}) {
              items {
              title
              created
              slug
              description
              mediumUrl
              header {
                url
              }
              content {
                json
              }
              category {
                name
              }
            }
          }
        }
      `,
    })

    if (posts.data.blogPostCollection.items[0]) {
      let post = JSON.parse(
        JSON.stringify(posts.data.blogPostCollection.items[0])
      )
      let currentPost: Document = JSON.parse(JSON.stringify(post.content.json))
      for (const node of currentPost.content) {
        const key = currentPost.content.indexOf(node)
        if (node.nodeType == BLOCKS.EMBEDDED_ASSET) {
          const result = await contentful.getAsset(node.data.target.sys.id)
          currentPost.content[key].data.embed = {}
          currentPost.content[key].data.embed.url = result.fields.file.url
          currentPost.content[key].data.embed.contentType =
            result.fields.file.contentType
          currentPost.content[key].data.embed.title = result.fields.title
        }
      }

      post.content.json = currentPost

      return {
        props: {
          post: post,
        },
        revalidate: 10,
      }
    } else {
      return {
        notFound: true,
      }
    }
  } else {
    return {
      notFound: true,
    }
  }
}

const getStaticPaths: GetStaticPaths = async () => {
  const posts = await apolloClient.query({
    query: gql`
      query {
        blogPostCollection {
          items {
            slug
          }
        }
      }
    `,
  })

  const paths = posts.data.blogPostCollection.items.map((item: Post) => {
    return {
      params: {
        slug: item.slug,
      },
    }
  })
  return { paths: paths, fallback: 'blocking' }
}

const Main = tw.div`
        pt-28 
        xl:mx-56
`

const Content = tw.div`
      border
      rounded
      flex
      flex-col
`

const Header = tw.div`
      w-full 
      h-80 
      mb-2 
      relative
`

const Title = tw.a`
      m-2 
      font-bold 
      text-xl
`

const ArticleContent = tw.div`
      m-2
`

const Date = tw.a`
      mt-1 
      mb-2 
      mx-2 
      text-sm 
      text-gray-500
`

const ShareButton = tw.a`
      m-2 
      text-sm 
      text-green-600
`

const PostPage = ({ post }: { post: Post }) => {
  const Text = ({ children }: { children: ReactNode }) => {
    return <p className="my-2">{children}</p>
  }

  const Paragraph = (node: ReactNode, children: ReactNode) => {
    return <Text>{children}</Text>
  }

  const EmbeddedAsset = (
    node: ReactNode & {
      data: { embed: { url: string; title: string; contentType: string } }
    },
    children: ReactNode
  ) => {
    if (
      node.data.embed &&
      node.data.embed.url &&
      node.data.embed.contentType.startsWith('image')
    ) {
      return (
        <div className={'mx-8 my-2 rounded'}>
          <img
            src={`https:${node.data.embed.url}`}
            alt={node.data.embed.title}
            className={'rounded'}
          />
        </div>
      )
    } else {
      return <>{children}</>
    }
  }

  const options: Options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: Paragraph,
      [BLOCKS.EMBEDDED_ASSET]: EmbeddedAsset as unknown as NodeRenderer,
    },
  }

  return (
    <>
      <Head>
        <title>Meerkt Blog | {post.title}</title>
        <meta property="og:title" content={`Meerkt Blog | ${post.title}`} />
        <meta property="og:description" content={post.description} />
        <meta property="og:image" content={post.header.url} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="og:site_name" content="Meerkt Blog" />
        <meta name="twitter:image:alt" content={post.title} />
        <meta
          property="og:url"
          content={`https://blog.meerkt.com/post/${post.slug}`}
        />
      </Head>
      <Main>
        <Content>
          <Header>
            <Image
              src={post.header.url}
              alt={post.title}
              layout={'fill'}
              className={'rounded-t  object-cover'}
            />
          </Header>
          <Title>{post.title}</Title>
          <div>
            <Date>{dayjs(post.created).format('MMM DD, YYYY')}</Date>
            <Link href={`/${post.category.name.toLowerCase()}`} passHref>
              <a className={'m-2 text-sm text-yellow-600'}>
                {post.category.name}
              </a>
            </Link>
            {post.mediumUrl && (
              <Link href={`${post.mediumUrl}`} passHref>
                <a className={'m-2 text-sm text-blue-600'}>
                  This post is also available on Medium
                </a>
              </Link>
            )}
          </div>
          <div>
            <Date>Share now :</Date>
            {process.browser && (
              <>
                <Link
                  href={`https://www.facebook.com/sharer/sharer.php?u=${document.location}`}
                  passHref
                >
                  <ShareButton>Facebook</ShareButton>
                </Link>
                <Link
                  href={`https://twitter.com/share?text=${post.title}&url=${document.location}`}
                  passHref
                >
                  <ShareButton>Twitter</ShareButton>
                </Link>
                <Link
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${document.location}`}
                  passHref
                >
                  <ShareButton>Linkedin</ShareButton>
                </Link>
              </>
            )}
          </div>
          <ArticleContent>
            {documentToReactComponents(post.content.json, options)}
          </ArticleContent>
        </Content>
      </Main>
    </>
  )
}

export default PostPage

export { getStaticPaths, getStaticProps }
